<template>
    <section aria-label="carousel">
        <p class="cz2__sr-only">
            {{ $t('SR_CAROUSEL') }}
        </p>

        <div
            :class="{ 'swiper-container-outer--has-bullets': totalPages > 1 }"
            class="swiper-container-outer"
        >
            <button
                type="button"
                ref="arrowLeft"
                :class="{
                    'has-mobile-bullets': hasMobileBullets,
                    'hide-mobile-arrows': !hasMobileArrows,
                }"
                class="swiper-custom-arrow swiper-custom-arrow--left"
                :title="$t('PREV_PAGE')"
                :aria-label="$t('PREV_PAGE')"
                :disabled="previousIsDisabled"
                @click="swipeArrow(-1)"
                :style="{ backgroundImage: `url('${chevronIcon}')` }"
            />

            <div
                ref="container"
                class="swiper-container"
                :class="{
                    'mask-left': currentPage === totalPages - 1 && showMask,
                    'mask-right': currentPage !== totalPages - 1 && totalPages > 1 && showMask,
                    'mask-left-to-right': swiper && swiper.progress > 0 && swiper.progress < 1 && showMask,
                    'swiper-container--no-padding': noPadding,
                }"
            >
                <div
                    class="swiper-wrapper"
                    ref="slides"
                    :is="isListElement ? 'ul' : 'div'"
                >
                    <slot name="slides" />
                </div>
            </div>

            <button
                type="button"
                ref="arrowRight"
                :class="{
                    'has-mobile-bullets': hasMobileBullets,
                    'hide-mobile-arrows': !hasMobileArrows,
                }"
                class="swiper-custom-arrow swiper-custom-arrow--right"
                :title="$t('NEXT_PAGE')"
                :aria-label="$t('NEXT_PAGE')"
                :disabled="nextIsDisabled"
                @click="swipeArrow(1)"
                :style="{ backgroundImage: `url('${chevronIcon}')` }"
            />
        </div>

        <ul
            :class="{ 'hide--below-short hide--below-md': !hasMobileBullets }"
            class="swiper-custom-bullets"
            ref="bullets"
            @click.prevent="swipeToBullet"
        />

        <p
            :class="{ 'hide--below-md': hasMobileBullets }"
            class="swiper-page-counter hide--above-md"
        >
            {{ currentPage + 1 }} / {{ totalPages }}
        </p>
    </section>
</template>

<style lang="scss">
    @import '../../style/variables';

    #cz2.cz2 {
        .swiper-container-outer {
            position: relative;

            margin: 0 auto var(--layout-spacing) auto;

            width: 100%;
        }

        ul {
            // Override list styles
            margin: 0;
            padding: 0;
            list-style: none;

            margin-top: var(--layout-spacing);
        }

        .swiper-container {
            overflow: hidden;

            padding: 4px;

            &.swiper-container--no-padding {
                padding: 0;
            }
        }

        .swiper-slide li:focus {
            outline: none;

            &:focus {
                outline: none;

                &:after {
                    content: '';

                    position: absolute;

                    left: -2px;
                    top: -2px;

                    width: 74px;
                    height: 74px;

                    border: 5px solid color(secondaryBlack);
                    border-radius: 8px;
                }
            }

            @include respond-above(lg) {
                &:focus {
                    &:after {
                        width: 94px;
                        height: 94px;
                    }
                }
            }
        }

        .swiper-custom-arrow {
            z-index: 2;
            position: absolute;
            top: calc(100% + 20px);

            &.has-mobile-bullets {
                top: 50%;
                margin-top: -20px;
            }

            &.hide-mobile-arrows {
                @include respond-below(sm) {
                    display: none !important;
                }
            }

            border-radius: 40px;
            width: 40px;
            height: 40px;

            background: #111;
            background-position-y: 55%;
            background-position-x: center;
            background-repeat: no-repeat;
            background-size: 50%;

            transition: opacity 0.2s ease-out;

            &:focus {
                outline: none;
                border: 3px solid #F3F3F7;
            }

            &.swiper-custom-arrow--left {
                transform: rotate(90deg);
                &.has-mobile-bullets {
                    right: calc(100% - 40px);
                }
            }

            &.swiper-custom-arrow--right {
                left: calc(100% - 40px);
                transform: rotate(-90deg);
            }

            &.swiper-custom-arrow--hidden {
                display: none;
            }

            &.swiper-custom-arrow--inactive {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }

        .swiper-page-counter {
            padding: var(--layout-spacing) 0;
            width: 100%;

            text-align: center;
            @include font(formBold);
            color: black;
        }

        .swiper-custom-bullets {
            display: flex;
            justify-content: center;
        }

        .swiper-custom-bullets__bullet {
            display: block;
            position: relative;

            // Increase the button size so the tap target would be a bit bigger.
            width: 32px;
            height: 32px;

            border: none;
            background: transparent;

            transition: all 0.2s ease-out;

            cursor: pointer;

            &.swiper-custom-bullets__bullet--active {
                &:before {
                    content: '';

                    position: absolute;
                    top: 3px;
                    left: 3px;

                    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.20));
                    border: 3px solid #111;
                    border-radius: 50%;

                    width: 26px;
                    height: 26px;
                }
            }

            &:after {
                content: '';

                position: absolute;

                left: 8px;
                top: 8px;

                width: 16px;
                height: 16px;

                border-radius: 50%;

                background: #111;
            }

            &:focus {
                outline: none;

                &:after {
                    border: 2px solid white;
                    width: 18px;
                    height: 18px;
                    top: 7px;
                    left: 7px;
                }
            }
        }

        --mask-width: 24px;

        .mask-left {
            -webkit-mask-image: linear-gradient(to left, rgba(0, 0, 0, 1) calc(100% - var(--mask-width)), rgba(0, 0, 0, 0) 100%);
            mask-image: linear-gradient(to left, rgba(0, 0, 0, 1) calc(100% - var(--mask-width)), rgba(0, 0, 0, 0) 100%);
        }

        .mask-right {
            -webkit-mask-image: linear-gradient(to right, rgba(0, 0, 0, 1) calc(100% - var(--mask-width)), rgba(0, 0, 0, 0) 100%);
            mask-image: linear-gradient(to right, rgba(0, 0, 0, 1) calc(100% - var(--mask-width)), rgba(0, 0, 0, 0) 100%);
        }

        .mask-left-to-right {
            mask-image: linear-gradient(to right, transparent 0%, black var(--mask-width), black calc(100% - var(--mask-width)), transparent 100%);
            -webkit-mask-image: linear-gradient(to right, transparent 0%, black var(--mask-width), black calc(100% - var(--mask-width)), transparent 100%);
        }

        @include respond-above(md) {
            .swiper-container-outer {
                padding: 0 var(--layout-spacing);
                width: calc(100% - 80px);
            }

            .swiper-custom-arrow,
            .swiper-custom-arrow.has-mobile-bullets {
                position: absolute;
                top: 50%;
                margin-top: -20px;

                &.swiper-custom-arrow--left {
                    right: 100%;
                }

                &.swiper-custom-arrow--right {
                    left: 100%;
                }
            }
        }
    }

    #cz2.cz2.cz2__is-sb {
        .swiper-custom-arrow--left {
            transform: rotate(0deg) !important;
            background-size: 100%;
        }

        .swiper-custom-arrow--right {
            transform: rotate(180deg);
            background-size: 100%;
        }

        .swiper-custom-arrow {
            background-color: transparent;
        }
    }
</style>

<script>
    // eslint-disable-next-line import/no-unresolved
    import Swiper from 'swiper/bundle';

    import chevron from '../../assets/icons/chevron-white.svg';
    import sbChevron from '../../assets/images/branded/sb-final/SB-Chevron L_M@2x.webp';

    export default {
        props: {
            focusedIndex: {
                type: Number,
                default: 0,
            },

            isListElement: {
                type: Boolean,
                default: false,
            },

            showMask: {
                type: Boolean,
                default: true,
            },

            spaceBetween: {
                type: Number,
                default: 16,
            },

            hasMobileBullets: {
                type: Boolean,
                default: false,
            },

            hasMobileArrows: {
                type: Boolean,
                default: true,
            },

            slidesPerView: {
                type: String,
                default: 'auto',
            },

            allowFreeMode: {
                type: Boolean,
                default: true,
            },

            allowSticky: {
                type: Boolean,
                default: false,
            },

            noPadding: {
                type: Boolean,
                default: false,
            },

            autoHeight: {
                type: Boolean,
                default: false,
            },

            autoPlay: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                localFocusedIndex: this.focusedIndex,

                currentPage: 0,
                totalPages: 0,

                swiper: null,

                previousIsDisabled: true,
                nextIsDisabled: false,

                focusedSlideIndex: 0,
                hasKeyEventListener: false,
            };
        },

        computed: {
            currentGroupCode() {
                return this.$store.state.customizer.currentGroup.code;
            },

            isSpellingBee() {
                return this.$store.state.isSpellingBee;
            },

            chevronIcon() {
                return this.isSpellingBee ? sbChevron : chevron;
            },
        },

        watch: {
            focusedIndex() {
                this.updateSwiper();
            },
        },

        updated() {
            this.updateSwiper();
        },

        mounted() {
            this.createSwiper();
            this.addKeyDownListener();
        },

        beforeDestroy() {
            this.removeKeyDownListener();
        },

        methods: {
            createSwiper() {
                let lastTranslate = null;

                if (this.swiper) {
                    lastTranslate = this.swiper.translate;

                    this.swiper.destroy();
                    this.swiper = null;
                }

                const swiperOptions = {
                    slidesPerView: this.slidesPerView === 'auto' ? 'auto' : parseInt(this.slidesPerView, 10),
                    centeredSlides: false,
                    centerInsufficientSlides: true,
                    spaceBetween: this.spaceBetween,
                    freeMode: this.allowFreeMode ? {
                        momentumRatio: 0.5,
                        sticky: this.allowSticky,
                    } : false,
                    loop: false,
                    on: {
                        touchStart: () => {
                            this.$store.dispatch('uiInteracted');
                        },
                        resize: () => {
                            if (this.$refs.slides) {
                                this.updateBullets();
                                this.updateActiveBullet();
                                this.updateAccessibilityAttributes();
                            }
                        },
                        progress: () => {
                            if (this.$refs.bullets) {
                                this.updateBullets();
                                this.updateActiveBullet();
                            }
                            setTimeout(() => {
                                this.updateAccessibilityAttributes();
                            }, 1000);
                        },
                    },
                    preventClicks: true,
                    threshold: 10,
                    autoHeight: this.autoHeight,
                    autoplay: this.autoPlay,
                };

                if (this.localFocusedIndex != null) {
                    swiperOptions.initialSlide = this.localFocusedIndex;
                }

                if (this.isKiosk) {
                    swiperOptions.passiveListeners = false;
                    swiperOptions.simulateTouch = false;
                }

                this.swiper = new Swiper(this.$refs.container, swiperOptions);

                this.updateBullets();
                this.updateActiveBullet();
                this.updateAccessibilityAttributes();
                this.updateTabIndexes();

                if (lastTranslate) {
                    this.swiper.setTranslate(lastTranslate);
                }
            },

            updateBullets() {
                const slidesWrapper = this.$refs.slides;
                const width = slidesWrapper.offsetWidth;
                const scrollableWidth = slidesWrapper.scrollWidth;

                const bulletsWrapper = this.$refs.bullets;
                const totalBullets = Math.ceil(scrollableWidth / width);

                // Clear old bullets.
                while (bulletsWrapper.firstChild) {
                    bulletsWrapper.removeChild(bulletsWrapper.lastChild);
                }

                if (totalBullets > 1) {
                    for (let b = 0; b < totalBullets; b += 1) {
                        // Why create bullets manually - we need Swiper to be "updated" in order to calculate
                        // the number of bullets based on width and the number of elements.
                        // If we use Vue to render bullets, we would need to set a data var to the number of bullets
                        // which triggers an update. Which would trigger a swiper update. Which would trigger
                        // a bullets update. ... infinite loop. So we just create bullet elements manually.

                        const bullet = document.createElement('li');
                        const bulletButton = document.createElement('button');
                        const bulletLabel = `Page ${b + 1} of ${totalBullets}`;
                        bulletButton.classList.add('swiper-custom-bullets__bullet');
                        bulletButton.dataset.progress = (b / (totalBullets - 1)).toFixed(4);
                        bulletButton.title = bulletLabel;
                        bulletButton.ariaLabel = bulletLabel;

                        bullet.appendChild(bulletButton);
                        bulletsWrapper.appendChild(bullet);
                    }
                }
            },

            updateActiveBullet() {
                if (!this.swiper) {
                    return;
                }

                // Remove all active bullets.
                const bullets = Array.from(this.$refs.bullets.querySelectorAll('.swiper-custom-bullets__bullet'));

                bullets.forEach((b) => {
                    b.classList.remove('swiper-custom-bullets__bullet--active');
                    b.ariaCurrent = false;
                });

                if (bullets.length < 2) {
                    // No bullets...

                    this.$refs.arrowLeft.classList.add('swiper-custom-arrow--hidden');
                    this.$refs.arrowRight.classList.add('swiper-custom-arrow--hidden');
                    return;
                }

                // Figure out active bullet index.
                this.currentPage = Math.round(this.swiper.progress * (bullets.length - 1));
                this.totalPages = bullets.length;

                // Handle the last page.
                if (this.currentPage > bullets.length - 1) {
                    this.currentPage = bullets.length - 1;
                }

                // Adjust arrows.
                this.$refs.arrowLeft.classList.remove('swiper-custom-arrow--hidden');
                if (this.currentPage === 0) {
                    this.$refs.arrowLeft.classList.add('swiper-custom-arrow--inactive');
                    this.previousIsDisabled = true;
                } else {
                    this.$refs.arrowLeft.classList.remove('swiper-custom-arrow--inactive');
                    this.previousIsDisabled = false;
                }

                this.$refs.arrowRight.classList.remove('swiper-custom-arrow--hidden');
                if (this.currentPage === bullets.length - 1) {
                    this.$refs.arrowRight.classList.add('swiper-custom-arrow--inactive');
                    this.nextIsDisabled = true;
                } else {
                    this.$refs.arrowRight.classList.remove('swiper-custom-arrow--inactive');
                    this.nextIsDisabled = false;
                }

                if (bullets[this.currentPage]) {
                    const currentBullet = bullets[this.currentPage];

                    currentBullet.classList.add('swiper-custom-bullets__bullet--active');
                    currentBullet.ariaCurrent = true;
                }
            },

            swipeToBullet(e) {
                if (this.swiper && e.target && e.target.dataset.progress != null) {
                    this.swiper.setProgress(parseFloat(e.target.dataset.progress), 250);
                }
            },

            swipeArrow(direction) {
                // Remove all active bullets.
                const bullets = Array.from(this.$refs.bullets.querySelectorAll('.swiper-custom-bullets__bullet'));

                if (bullets.length < 2) {
                    // No bullets...
                    return;
                }

                // Figure out active bullet index.
                this.currentPage = Math.round(this.swiper.progress * (bullets.length - 1));
                this.totalPages = bullets.length;

                this.currentPage += direction;

                // Handle the last page.
                if (this.currentPage > bullets.length - 1) {
                    this.currentPage = bullets.length - 1;
                }

                if (this.currentPage < 0) {
                    this.currentPage = 0;
                }

                const bullet = bullets[this.currentPage];

                if (bullet) {
                    this.swiper.setProgress(parseFloat(bullet.dataset.progress), 250);
                }
            },

            updateSwiper() {
                if (!this.swiper) {
                    return;
                }

                this.swiper.update();

                if (this.localFocusedIndex !== this.focusedIndex) {
                    const scrollToIndex = this.focusedIndex;

                    this.swiper.slideTo(scrollToIndex);
                    this.localFocusedIndex = scrollToIndex;
                }

                this.updateBullets();
                this.updateActiveBullet();
                this.updateAccessibilityAttributes();
            },

            updateTabIndexes() {
                const slidesWrapper = this.$refs.slides;
                const buttons = Array.from(slidesWrapper.getElementsByClassName('swiper-slide'));
                const focusedButton = buttons[this.focusedSlideIndex];

                buttons?.forEach((button) => {
                    button.setAttribute('tabindex', -1);
                });

                if (focusedButton) {
                    focusedButton.setAttribute('tabindex', 0);
                }
            },

            updateAccessibilityAttributes() {
                const slidesWrapper = this.$refs.slides;

                if (slidesWrapper) {
                    const slides = slidesWrapper.getElementsByClassName('swiper-slide');

                    // Remove unnecessary attribute added by Swiper
                    slidesWrapper.removeAttribute('aria-live');

                    slides.forEach((slide) => {
                        const button = slide.querySelector('button');

                        if (this.isVisible(slide)) {
                            slide.setAttribute('aria-hidden', false);
                            slide.removeAttribute('tabindex');
                            button?.removeAttribute('tabindex');
                        } else {
                            slide.setAttribute('aria-hidden', true);
                            slide.setAttribute('tabindex', -1);
                            button?.setAttribute('tabindex', -1);
                        }
                    });
                }
            },

            addKeyDownListener() {
                if (!this.hasKeyEventListener) {
                    this.$refs.slides.addEventListener('keydown', this.captureNavKeyPress);
                    this.hasKeyEventListener = true;
                }
            },

            removeKeyDownListener() {
                this.$refs.slides.removeEventListener('keydown', this.captureNavKeyPress);
                this.hasKeyEventListener = false;
            },

            captureNavKeyPress(event) {
                const visibleButtons = this.getVisibleButtons();
                const firstOption = visibleButtons[0];
                const lastOption = visibleButtons[visibleButtons.length - 1];
                const focusedOptionElement = visibleButtons[this.focusedSlideIndex];

                if (event.keyCode === 40 || event.keyCode === 39) {
                    // Down or right arrow key
                    event.preventDefault();

                    if (focusedOptionElement && visibleButtons[this.focusedSlideIndex + 1]) {
                        this.focusedSlideIndex += 1;
                        this.updateTabIndexes();
                        visibleButtons[this.focusedSlideIndex].focus();
                    } else {
                        this.focusedSlideIndex = 0;
                        this.updateTabIndexes();
                        firstOption.focus();
                    }
                } else if (event.keyCode === 38 || event.keyCode === 37) {
                    // Up or left arrow key
                    event.preventDefault();

                    if (focusedOptionElement && visibleButtons[this.focusedSlideIndex - 1]) {
                        this.focusedSlideIndex -= 1;
                        this.updateTabIndexes();
                        visibleButtons[this.focusedSlideIndex].focus();
                    } else {
                        this.focusedSlideIndex = visibleButtons.length - 1;
                        this.updateTabIndexes();
                        lastOption.focus();
                    }
                }
            },

            getVisibleButtons() {
                const wrapper = this.$refs.slides;
                const allButtons = wrapper.getElementsByClassName('swiper-button');
                const visibleButtons = [];

                allButtons?.forEach((button) => {
                    if (this.isVisible(button)) {
                        visibleButtons.push(button);
                    }
                });

                return visibleButtons;
            },

            isVisible(element) {
                const slidesContainer = this.$refs.container;
                const wrapperRect = slidesContainer.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();

                return (
                    elementRect.top >= wrapperRect.top
                    && elementRect.left >= wrapperRect.left
                    && (elementRect.left <= wrapperRect.right && (elementRect.width / 2 + elementRect.left <= wrapperRect.right))
                    && elementRect.bottom <= wrapperRect.bottom
                );
            },
        },
    };
</script>
